import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { SearchIcon, CreditCardIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, DollarSignIcon, CheckIcon, XIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface CardOnFilePaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSuccess?: () => void;
}
interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  metadata: {
    ltv: string;
    since: string;
    status: string;
  };
  cards: Card[];
}
interface Card {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isExpired: boolean;
}
const mockCustomers: Customer[] = [{
  id: 'cus_1',
  name: 'John Smith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  metadata: {
    ltv: `${getTenant().currency.code} 45,000`,
    since: 'Jan 2023',
    status: 'Premium'
  },
  cards: [{
    id: 'card_1',
    brand: 'Visa',
    last4: '4242',
    expiry: '12/27',
    isExpired: false
  }, {
    id: 'card_2',
    brand: 'Mastercard',
    last4: '5555',
    expiry: '01/22',
    isExpired: true
  }, {
    id: 'card_3',
    brand: 'Amex',
    last4: '9012',
    expiry: '06/28',
    isExpired: false
  }, {
    id: 'card_4',
    brand: 'Visa',
    last4: '1881',
    expiry: '03/24',
    isExpired: true
  }]
}, {
  id: 'cus_2',
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  phone: '+1 (555) 987-6543',
  metadata: {
    ltv: `${getTenant().currency.code} 12,500`,
    since: 'Mar 2024',
    status: 'Standard'
  },
  cards: [{
    id: 'card_5',
    brand: 'Mastercard',
    last4: '3000',
    expiry: '09/26',
    isExpired: false
  }, {
    id: 'card_6',
    brand: 'Visa',
    last4: '7744',
    expiry: '11/23',
    isExpired: true
  }, {
    id: 'card_7',
    brand: 'Amex',
    last4: '2200',
    expiry: '04/28',
    isExpired: false
  }]
}, {
  id: 'cus_3',
  name: 'Michael Brown',
  email: 'michael@example.com',
  phone: '+1 (555) 456-7890',
  metadata: {
    ltv: `${getTenant().currency.code} 8,200`,
    since: 'Oct 2024',
    status: 'New'
  },
  cards: [{
    id: 'card_8',
    brand: 'Visa',
    last4: '6633',
    expiry: '02/27',
    isExpired: false
  }, {
    id: 'card_9',
    brand: 'Mastercard',
    last4: '4400',
    expiry: '08/23',
    isExpired: true
  }, {
    id: 'card_10',
    brand: 'Visa',
    last4: '1199',
    expiry: '05/24',
    isExpired: true
  }]
}];
export function CardOnFilePayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSuccess
}: CardOnFilePaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const filteredCustomers = searchTerm.trim() ? mockCustomers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm)) : mockCustomers;
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setIsDropdownOpen(false);
    const validCards = customer.cards.filter(c => !c.isExpired);
    setSelectedCardId(validCards.length > 0 ? validCards[0].id : null);
  };
  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSelectedCardId(null);
    setSearchTerm('');
  };
  const handleConfirmCharge = () => {
    setShowPreviewModal(false);
    onSuccess?.();
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const validCards = selectedCustomer?.cards.filter(c => !c.isExpired) || [];
  const selectedCard = selectedCustomer?.cards.find(c => c.id === selectedCardId);
  return <div className="space-y-5" data-id="element-7896" data-mp="3opnkz6g16iat">
      {/* ─── CUSTOMER SELECTION ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7897" data-mp="39zzk26brhlnm">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7898" data-mp="3eyauai5yxy6d">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7899" data-mp="3eyszw5eitoqy">
            <UserIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7900" data-mp="6jsrovysrau5" />
            {t('components.BulkPaymentPreview.customer')}
          </h2>
        </div>

        {!selectedCustomer ? <div className="p-6" ref={dropdownRef} data-id="element-7901" data-mp="3sqhb86gy496v">
            <div className="relative" data-id="element-7902" data-mp="3ss231obvle11">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7903" data-mp="1ilj4uoty7mf">
                <SearchIcon size={16} className="text-gray-400" data-id="element-7904" data-mp="2ha9ckg4mxi0h" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.CardOnFilePayment.attrs.search_by_name_email_or_phone')} value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setIsDropdownOpen(true);
          }} onFocus={() => setIsDropdownOpen(true)} data-id="element-7905" data-mp="2ma56fmjmbt2b" />

              {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-7906" data-mp="lmkensb6soch">
                  {filteredCustomers.length > 0 ? <div data-id="element-7907" data-mp="ue2x69vlvg8q">
                      {filteredCustomers.map((customer, idx) => <div key={customer.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-100' : ''}`} onMouseDown={e => {
                e.preventDefault();
                handleSelectCustomer(customer);
              }} data-id="element-7908" data-mp="pmuiozff54op" data-mpi={idx}>
                          <div className="flex justify-between items-center" data-id="element-7909" data-mp="rrn8arf9zndm">
                            <div data-id="element-7910" data-mp="vq2qmi0ew7kp">
                              <p className="text-sm font-medium text-gray-900" data-id="element-7911" data-mp="10qh1e7tedvmf">
                                {customer.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-7912" data-mp="15p1za0l6g37p">
                                {customer.email}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400" data-id="element-7913" data-mp="1ffwpfg1jnmyf">
                              {customer.cards.filter(c => !c.isExpired).length}{' '}
                              {t('components.CardOnFilePayment.cards')}
                            </span>
                          </div>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-7914" data-mp="1yj8bvmlzcg62">
                      {t('components.CardOnFilePayment.no_customers_found')}
                    </div>}
                </div>}
            </div>
          </div> : <div className="px-6 py-4 flex items-center justify-between" data-id="element-7915" data-mp="2m8j42fp9l28l">
            <div className="flex items-center min-w-0" data-id="element-7916" data-mp="2har8nbpeuxzn">
              <div className="h-9 w-9 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-7917" data-mp="2hb9en01vkqkr">
                <UserIcon size={16} className="text-[#9F140F]" data-id="element-7918" data-mp="2cherfmn29tgx" />
              </div>
              <div className="min-w-0" data-id="element-7919" data-mp="28g35ps7b5se0">
                <div className="flex items-center gap-2" data-id="element-7920" data-mp="350lps5grj8pp">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-7921" data-mp="3526j5rrccllz">
                    {selectedCustomer.name}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#E2231A]/10 text-[#E2231A] flex-shrink-0" data-id="element-7922" data-mp="1fjbk8ly1vpty">
                    {selectedCustomer.metadata.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500" data-id="element-7923" data-mp="1oiov9j1tomgs">
                  <span className="flex items-center" data-id="element-7924" data-mp="1jkdlt9ffwdz3">
                    <MailIcon size={12} className="mr-1 text-gray-400" data-id="element-7925" data-mp="1yi605y992wbp" />
                    {selectedCustomer.email}
                  </span>
                  <span className="flex items-center" data-id="element-7926" data-mp="1tjmdtwqqviit">
                    <PhoneIcon size={12} className="mr-1 text-gray-400" data-id="element-7927" data-mp="27bizpj1tkw6r" />
                    {selectedCustomer.phone}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={handleClearCustomer} className="text-xs font-medium text-[#9F140F] hover:text-[#9F140F]/75 transition-colors flex-shrink-0 ml-4" data-id="element-7928" data-mp="2h8nqjw6m1ftw">
              {t('components.CardOnFilePayment.change')}
            </button>
          </div>}
      </div>

      {/* ─── PAYMENT METHOD ─── */}
      {selectedCustomer && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7929" data-mp="2ly87e8686fo8">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7930" data-mp="2qx2p16sghy6r">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7931" data-mp="2qvhyds99ize6">
              <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7932" data-mp="2d4wbztklo7bj" />
              {t('components.CardOnFilePayment.payment_method')}
            </h3>
          </div>

          {selectedCustomer.cards.length > 0 ? <div data-id="element-7933" data-mp="239bdw6injr72">
              {selectedCustomer.cards.map((card, idx) => <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${card.isExpired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCardId === card.id ? 'bg-[#E2231A]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
          if (!card.isExpired) setSelectedCardId(card.id);
        }} data-id="element-7934" data-mp="287cu8jop1ceu" data-mpi={idx}>
                  <input type="radio" name="savedCard" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedCardId === card.id} disabled={card.isExpired} onChange={() => {
            if (!card.isExpired) setSelectedCardId(card.id);
          }} data-id="element-7935" data-mp="3r5iwjxwi8kgj" />

                  <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-7936" data-mp="9pj5a4xv0n40">
                    <div className="flex items-center" data-id="element-7937" data-mp="2ygbxmxt51sur">
                      <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${card.isExpired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-7938" data-mp="2yhwwgzmhjxyf">
                        {card.brand.toLowerCase() === 'visa' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/visa_logo.png" alt={t('components.CardOnFilePayment.attrs.visa')} className="h-4 object-contain" data-id="element-7939" data-mp="1dw6upu83ygqk" /> : card.brand.toLowerCase() === 'mastercard' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/mastercard_logo.png" alt={t('components.CardOnFilePayment.attrs.mastercard')} className="h-6 object-contain" data-id="element-7940" data-mp="21p6ylwkm9h96" /> : card.brand.toLowerCase() === 'amex' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/amex_logo.svg" alt={t('components.CardOnFilePayment.attrs.amex')} className="h-6 object-contain" data-id="element-7941" data-mp="384sk8841pbvz" /> : <span className={`text-[10px] font-bold uppercase ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7942" data-mp="2pgnlxg65qj4k">
                            {card.brand}
                          </span>}
                      </div>
                      <div data-id="element-7943" data-mp="2blbfibin347t">
                        <p className={`text-sm font-medium ${card.isExpired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-7944" data-mp="26okz1n2xa6lv">
                          {card.brand} •••• {card.last4}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-7945" data-mp="qyll9cuxsuk4">
                          <p className={`text-xs ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7946" data-mp="c95sjrbuif1s">
                            {t('components.CardOnFilePayment.expires')}{' '}
                            {card.expiry}
                          </p>
                          {card.isExpired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-7947" data-mp="vw3smytb31q0">
                              {t('components.CardOnFilePayment.expired')}
                            </span>}
                        </div>
                      </div>
                    </div>
                    {!card.isExpired && selectedCardId === card.id && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-7948" data-mp="1r4xovm136htm" />}
                  </div>
                </div>)}
            </div> : <div className="px-6 py-10 text-center" data-id="element-7949" data-mp="1hdb4p06rt8eq">
              <AlertCircleIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-7950" data-mp="1ch2rhsc4vpa7" />

              <p className="text-sm font-medium text-gray-700" data-id="element-7951" data-mp="18gahgwvftgcv">
                {t('components.CardOnFilePayment.no_cards_on_file')}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-7952" data-mp="yg05tgp1wgzt">
                {t('components.CardOnFilePayment.this_customer_has_no_saved_cards')}
              </p>
            </div>}
        </div>}

      {/* ─── CHARGE DETAILS ─── */}
      {selectedCustomer && selectedCardId && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7953" data-mp="2nwricusid8ga">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7954" data-mp="2wbfdisj02tc8">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7955" data-mp="30e2831722sd4">
              <DollarSignIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7956" data-mp="30bpic1v23dpi" />
              {t('components.CardOnFilePayment.charge_details')}
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-7957" data-mp="3a3dfmjeanoyw">
            <div data-id="element-7958" data-mp="2ddmbrtk44pgf">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7959" data-mp="2de4dv6tghxw9">
                {t('components.CardOnFilePayment.payment_amount')}{' '}
                <span className="text-red-400" data-id="element-7960" data-mp="2meao8jtuyhr7">*</span>
              </label>
              <div className="relative" data-id="element-7961" data-mp="2mgdorxsmzta6">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7962" data-mp="2rdx28vhig212">
                  <span className="text-sm font-medium text-gray-500" data-id="element-7963" data-mp="1ole2k43nk4u">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </span>
                </div>
                <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-7964" data-mp="rqxmtxfgyybi" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7965" data-mp="wqk0u934gild">
              <div data-id="element-7966" data-mp="wo7ap8n7n1x8">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7967" data-mp="1bf6iihg81mfr">
                  {t('components.BulkPaymentPreview.reference')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_invoice_1234')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-7968" data-mp="1kfw6iyz8q8ix" />
              </div>
              <div data-id="element-7969" data-mp="3h9zbheg70mcw">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7970" data-mp="3haiwx551xsvo">
                  {t('components.BulkPaymentPreview.description')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_monthly_subscription')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-7971" data-mp="21r1fk7phe5eb" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── ADDITIONAL OPTIONS ─── */}
      {selectedCustomer && selectedCardId && <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} hideRedirect={true} data-id="element-7972" data-mp="1909nnmfg82ws" />}

      {/* ─── PREVIEW CHARGE BUTTON ─── */}
      {selectedCustomer && selectedCardId && <div className="flex justify-end" data-id="element-7973" data-mp="1scy6ol2co2yg">
          <button onClick={() => {
        if (selectedCustomer && selectedCardId && amount) {
          setShowPreviewModal(true);
        }
      }} disabled={!amount} className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-7974" data-mp="1sdzhcofgenfn">
            {t('components.CardOnFilePayment.preview_charge')}
            <ChevronRightIcon size={16} className="ml-1.5" data-id="element-7975" data-mp="21cbj9mlrt7ne" />
          </button>
        </div>}

      {/* ─── PREVIEW MODAL ─── */}
      {showPreviewModal && selectedCustomer && selectedCard && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-7976" data-mp="pz93gtrhwm4a">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-7977" data-mp="2z8giv7y0a4wj">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-7978" data-mp="3d5c4dzvg1oth">
                <h3 className="text-base font-semibold text-gray-900 flex items-center" data-id="element-7979" data-mp="392fi9z6p3aks">
                  <ShieldCheckIcon size={18} className="mr-2 text-[#9F140F]" data-id="element-7980" data-mp="2lf7fbnwqodlx" />
                  {t('components.CardOnFilePayment.confirm_charge')}
                </h3>
                <button onClick={() => setShowPreviewModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7981" data-mp="2u8azef1w3opj">
                  <XIcon size={18} aria-hidden="true" data-id="element-7982" data-mp="21pf6vn9jhwkn" />
                </button>
              </div>

              <div className="p-6 space-y-5" data-id="element-7983" data-mp="1xov7se5eoivv">
                <div className="text-center pb-2" data-id="element-7984" data-mp="p0fvz5bto92">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-7985" data-mp="o7gjj8p610y">
                    {t('components.BulkPaymentPreview.amount')}
                  </p>
                  <p className="text-3xl font-bold text-gray-900" data-id="element-7986" data-mp="afv59cdvd3yq">
                    {currency} {amount}
                  </p>
                </div>

                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" data-id="element-7987" data-mp="3dvadq96ln5hr">
                  <div className="flex justify-between items-start px-4 py-3 bg-gray-50/50" data-id="element-7988" data-mp="3iu3ohtui4ie8">
                    <span className="text-sm text-gray-500" data-id="element-7989" data-mp="3ntq3o8tw48po">
                      {t('components.BulkPaymentPreview.customer')}
                    </span>
                    <div className="text-right" data-id="element-7990" data-mp="3su4axono3emn">
                      <p className="text-sm font-medium text-gray-900" data-id="element-7991" data-mp="133hg95pffavm">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-7992" data-mp="180r62mgq9ojz">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7993" data-mp="2nml0bin78w7j">
                    <span className="text-sm text-gray-500" data-id="element-7994" data-mp="2iti6d9eifkqt">
                      {t('components.CardOnFilePayment.card')}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-7995" data-mp="2dtm8lrweopjp">
                      {selectedCard.brand} •••• {selectedCard.last4}
                    </span>
                  </div>
                  {reference && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7996" data-mp="1zv5u3esaxur0">
                      <span className="text-sm text-gray-500" data-id="element-7997" data-mp="1ux4a8rn1xreh">
                        {t('components.BulkPaymentPreview.reference')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-7998" data-mp="3u7m5etzyfz2q">
                        {reference}
                      </span>
                    </div>}
                  {description && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7999" data-mp="2qyj79mz68l0i">
                      <span className="text-sm text-gray-500" data-id="element-8000" data-mp="2cza2rkkbvfcd">
                        {t('components.BulkPaymentPreview.description')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-8001" data-mp="2hw9z33rdl3qa">
                        {description}
                      </span>
                    </div>}
                </div>

                <div className="bg-[#FEE7E5] border border-[#FBD4D1] rounded-lg p-3 flex items-start" data-id="element-8002" data-mp="2821jthrcv7a2">
                  <AlertCircleIcon size={16} className="text-[#F1786F] mr-2 mt-0.5 flex-shrink-0" data-id="element-8003" data-mp="1u6pcmaw0joc9" />

                  <p className="text-xs text-[#EB4A42]" data-id="element-8004" data-mp="1kbnnvg5ngoa7">
                    {t('components.CardOnFilePayment.this_will_immediately_charge_the_customers')}
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3" data-id="element-8005" data-mp="11mbuz8z2wi4i">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-8006" data-mp="1fm3e1o4gcopd">
                  {t('components.CardOnFilePayment.cancel')}
                </button>
                <button onClick={handleConfirmCharge} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-8007" data-mp="1kko6309jw22q">
                  <CheckIcon size={16} className="mr-1.5" data-id="element-8008" data-mp="289z5oef5zqfa" />
                  {t('components.CardOnFilePayment.confirm_charge_2')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}